import { ArrowRight, ArrowUpRight, Instagram, Youtube, Mail } from "lucide-react";

/**
 * The holding page — an architect's editorial.
 *
 * A slim masthead (brand left, discipline right). A hero that opens on the line
 * "you found me" in one calligraphic sweep, her working photograph beside it, a
 * short intro, an outlined Stay in the loop button, and her socials. Then the
 * worlds she is building as arched portals, and a quiet "Thank you for being
 * here" sign-off. Two typefaces, warm ground, hairlines, restraint.
 *
 * Stay in the loop opens a prefilled email (no backend). No contact form.
 */

const UTM = "utm_source=shruthiravi&utm_medium=comingsoon";
const TALK_URL = "https://www.youtube.com/watch?v=0OJG-Lab4Yk";
const STUDIO_EMAIL = "shruthiravioli@gmail.com";
const LOOP_MAILTO = `mailto:${STUDIO_EMAIL}?subject=${encodeURIComponent(
  "Launch list"
)}&body=${encodeURIComponent("Please add me to the launch list.")}`;

/* terracotta2 (#AE5533) clears the large-text bar on this ground but dips
   under AA at caption sizes; this deeper cut carries the small labels. */
const ACCENT_DEEP = "#994626";

const BIO =
  "I'm Shruthi Ravi, an architect and interior designer, a TEDx speaker, and Women Entrepreneur of the Year. I create spaces that are sustainable, soulful, and deeply connected to nature and the people who experience them. Alongside design, I coach and build communities that empower women to rise.";

const BUSINESSES = [
  { name: "Firefly", img: "firefly-treehouse.jpg", line: "A treehouse escape in the trees.", href: "https://www.instagram.com/fireflytreehouse/" },
  { name: "Earth Alive", img: "ea-01.jpg", line: "An 11-acre sustainable living space.", href: `https://earthalive.in/?${UTM}` },
  { name: "Jayra Studio", img: "biz-jayra.jpg", line: "Sustainable, free-spirited fashion.", href: "https://www.instagram.com/jayra_studio/" },
  { name: "Balcony Bar", img: "biz-balconybar.jpg", line: "Good food and unforgettable nights.", href: `https://www.thebalconybar.in/reservations?${UTM}` },
  { name: "Push Up Bra", img: "biz-pushupbra.jpg", line: "A women's circle that lifts and holds.", href: "https://www.instagram.com/pushupbra_blr/" },
  { name: "The Teal Door", img: "td-01.jpg", line: "Coming soon", soon: true },
];

const SOCIALS = [
  { key: "ig", Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/shruthi_shakirra/" },
  { key: "yt", Icon: Youtube, label: "Watch her TEDx talk", href: TALK_URL },
  { key: "mail", Icon: Mail, label: "Email", href: `mailto:${STUDIO_EMAIL}` },
];

const STREAKS = {
  backgroundImage:
    "linear-gradient(112deg, rgba(255,252,244,0) 6%, rgba(255,251,240,0.4) 13%, rgba(151,120,82,0.045) 21%, rgba(255,252,244,0) 29%, rgba(255,251,240,0.34) 38%, rgba(151,120,82,0.05) 46%, rgba(255,252,244,0) 55%, rgba(255,250,238,0.3) 66%, rgba(151,120,82,0.04) 75%, rgba(255,252,244,0) 86%)",
};

const LABEL = { letterSpacing: "0.24em", fontWeight: 600 };

function Monogram({ scale = 1 }) {
  const s = (n) => `${n * scale}px`;
  return (
    <span className="relative block select-none" style={{ height: s(38), width: s(42) }} aria-hidden="true">
      <span className="absolute left-0 top-0 font-display leading-none" style={{ fontSize: s(31), fontWeight: 500 }}>S</span>
      <span className="absolute font-display leading-none" style={{ left: s(13), top: s(10), fontSize: s(31), fontWeight: 500 }}>R</span>
    </span>
  );
}

function SocialIcon({ s }) {
  const base = "grid h-11 w-11 place-items-center rounded-full border border-ink/15 text-ink transition-all duration-300";
  if (!s.href) {
    return (
      <span className={`${base} opacity-45`} aria-hidden="true" title={`${s.label} — coming soon`}>
        <s.Icon size={16} strokeWidth={1.5} />
      </span>
    );
  }
  const external = s.href.startsWith("http");
  return (
    <a
      href={s.href}
      aria-label={s.label}
      className={`${base} hover:-translate-y-0.5 hover:border-terracotta2/60 hover:text-terracotta2`}
      {...(external ? { target: "_blank", rel: "noopener" } : {})}
    >
      <s.Icon size={16} strokeWidth={1.5} />
    </a>
  );
}

function Portal({ b }) {
  const inner = (
    <>
      <div className="relative mx-auto w-full max-w-[184px]">
        <div className="relative aspect-[3/4] w-full overflow-hidden rounded-t-full ring-1 ring-ink/10">
          <img
            src={`/portfolio/img/${b.img}`}
            alt={b.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.06]"
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-t-full"
            style={{ background: "linear-gradient(180deg, rgba(28,27,26,0) 52%, rgba(28,27,26,0.26))" }}
          />
        </div>
      </div>
      <p
        className="mt-5 inline-flex items-center justify-center gap-1 font-display uppercase text-[14px] text-ink transition-colors duration-300 group-hover:text-terracotta2"
        style={{ letterSpacing: "0.15em", fontWeight: 500 }}
      >
        {b.name}
        {b.href && <ArrowUpRight size={12} className="transition-transform duration-300 group-hover:rotate-45" aria-hidden="true" />}
      </p>
      {b.soon ? (
        <p className="mt-1.5 font-sans uppercase text-[10.5px]" style={{ letterSpacing: "0.2em", fontWeight: 600, color: ACCENT_DEEP }}>
          Coming soon
        </p>
      ) : (
        <p className="mx-auto mt-1.5 max-w-[22ch] font-sans text-[12.5px] leading-snug text-ink2">{b.line}</p>
      )}
    </>
  );
  const cls = "block text-center";
  return b.href ? (
    <a href={b.href} target="_blank" rel="noopener" className={`group ${cls}`} aria-label={`${b.name}, opens in a new tab`}>
      {inner}
    </a>
  ) : (
    <div className={`group ${cls}`}>{inner}</div>
  );
}

export default function ComingSoon() {
  return (
    <main id="top" className="relative min-h-screen bg-[#F0EADF] text-ink overflow-x-clip" data-testid="coming-soon">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={STREAKS} />

      <div className="relative mx-auto max-w-[1240px] px-6 sm:px-8 md:px-12">
        {/* ---- Masthead: brand left, discipline right ---- */}
        <header className="enter flex items-center justify-between gap-4 border-b border-ink/12 pt-8 pb-5 lg:pt-9">
          <a href="/" className="flex items-center gap-3.5" aria-label="Shruthi Ravi, home">
            <Monogram />
            <span className="hidden h-6 w-px bg-ink/20 sm:block" aria-hidden="true" />
            <span className="font-display uppercase text-[14px] sm:text-[15px] text-ink" style={{ letterSpacing: "0.32em", fontWeight: 500 }}>
              Shruthi Ravi
            </span>
          </a>
          <p className="hidden font-sans uppercase text-[10px] text-ink2 md:block" style={{ letterSpacing: "0.24em", fontWeight: 500 }}>
            Architect &nbsp;·&nbsp; Interiors &nbsp;·&nbsp; Founder
          </p>
        </header>

        {/* ---- Hero: one calligraphic line, an even stack, the working photograph ---- */}
        <div className="flex flex-col lg:grid lg:grid-cols-12 lg:items-center lg:gap-x-14 lg:pt-6">
          {/* Photograph — contained below lg so it can't balloon; a full column on
              desktop with its inner edge dissolving into the ground */}
          <figure
            className="enter order-1 relative mt-8 lg:order-none lg:col-start-7 lg:col-span-6 lg:row-start-1 lg:mt-0 lg:[mask-image:linear-gradient(to_right,transparent,black_12%)] lg:[-webkit-mask-image:linear-gradient(to_right,transparent,black_12%)]"
            style={{ animationDelay: "0.12s" }}
          >
            <img
              src="/portfolio/img/hero-shruthi.jpg"
              alt="Shruthi Ravi, architect and interior designer, in her studio"
              width={1127}
              height={1400}
              fetchPriority="high"
              className="w-[min(86vw,420px)] aspect-[4/5] object-cover object-[50%_28%] lg:h-[clamp(540px,64vh,620px)] lg:w-full lg:max-w-none lg:aspect-auto"
            />
          </figure>

          {/* Text — one continuous stack, vertically centred against the photo, with an
              even rhythm from the headline down through the intro to the call to act */}
          <div className="enter order-2 mt-10 lg:order-none lg:col-start-1 lg:col-span-6 lg:row-start-1 lg:mt-0 lg:pr-6" style={{ animationDelay: "0.06s" }}>
            <h1
              className="font-display leading-[1.02] tracking-[-0.02em] whitespace-nowrap text-[clamp(38px,11vw,72px)] lg:text-[clamp(56px,5.8vw,72px)]"
              style={{ fontWeight: 400 }}
            >
              you found me
            </h1>
            <p className="mt-8 max-w-[46ch] font-sans text-[15.5px] leading-[1.75] text-[#3F3B36]">{BIO}</p>
            <a
              href={TALK_URL}
              target="_blank"
              rel="noopener"
              className="group mt-5 inline-flex items-center gap-1.5 font-sans uppercase text-[12px] text-ink2 transition-colors duration-300 hover:text-terracotta2"
              style={{ letterSpacing: "0.16em", fontWeight: 600 }}
            >
              Watch my TEDx talk
              <ArrowUpRight size={13} strokeWidth={1.75} className="transition-transform duration-300 group-hover:rotate-45" aria-hidden="true" />
            </a>
            <p className="mt-7 font-display text-terracotta2 text-[23px] leading-snug sm:text-[25px]" style={{ fontWeight: 400 }}>
              Let&rsquo;s build beautiful, meaningful spaces, together.
            </p>
            <div className="mt-9">
              <a
                href={LOOP_MAILTO}
                data-testid="loop-cta"
                className="group flex w-[min(100%,340px)] items-center justify-between gap-8 border border-ink/60 px-7 py-4 font-sans uppercase text-[11.5px] text-ink transition-colors duration-300 hover:border-terracotta2 hover:bg-terracotta2 hover:text-cream"
                style={{ letterSpacing: "0.22em", fontWeight: 600 }}
              >
                Stay in the loop
                <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <div className="mt-6 flex items-center gap-2.5">
                {SOCIALS.map((s) => (
                  <SocialIcon key={s.key} s={s} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ---- The worlds she is building ---- */}
        <section className="mt-20 lg:mt-28">
          <div className="enter flex items-end justify-between border-b border-ink/15 pb-5">
            <h2 className="font-display text-[26px] leading-none text-ink sm:text-[32px]" style={{ fontWeight: 500 }}>
              The worlds I&rsquo;m building
            </h2>
            <span className="hidden font-sans uppercase text-[10px] text-ink2 sm:block" style={LABEL}>Six, and counting</span>
          </div>
          <div className="enter mt-12 grid grid-cols-2 gap-x-6 gap-y-12 sm:grid-cols-3 sm:gap-x-8 lg:grid-cols-6 lg:gap-x-6" style={{ animationDelay: "0.06s" }}>
            {BUSINESSES.map((b) => (
              <Portal key={b.name} b={b} />
            ))}
          </div>
        </section>

        {/* ---- Sign-off ---- */}
        <footer className="mt-24 border-t border-ink/15 pt-14 pb-16 text-center lg:mt-32">
          <p className="font-display text-terracotta2 text-[28px] leading-tight sm:text-[34px]" style={{ fontWeight: 400 }}>
            Thank you for being here.
          </p>
          <p className="mt-2 font-display italic text-terracotta2 text-[20px]" style={{ fontWeight: 400 }}>
            Shruthi
          </p>
          <p className="mt-10 font-mono uppercase text-[9.5px] text-ink2/80" style={{ letterSpacing: "0.22em" }}>
            © {new Date().getFullYear()} Shruthi Ravi &nbsp;·&nbsp; Bengaluru · Goa · Austin
          </p>
        </footer>
      </div>
    </main>
  );
}
